// src/shared/components/BatchTagModal.tsx

import { useMemo, useState } from 'react';
import { Modal, Stack, SegmentedControl, Text, Group, Button, Alert } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAlertTriangle, IconTags } from '@tabler/icons-react';
import { TagInput } from './TagInput';
import {
  TagBatchUtils,
  type BatchTagItem,
  type BatchTagMode,
  type BatchTagPlan,
} from '@/features/tag/TagBatchUtils';

interface BatchTagModalProps {
  opened: boolean;
  onClose: () => void;
  /** The selected media items (id, title, current tags). */
  items: BatchTagItem[];
  /** Singular noun for messaging, e.g. 'video' or 'stream'. */
  entityLabel: string;
  /** Account-wide tag list — add-mode suggestions and canonical casing. */
  allTags: string[];
  isPending: boolean;
  /** Called after the user confirms; parent runs the mutation and closes on success. */
  onApply: (plan: BatchTagPlan, mode: BatchTagMode, tags: string[]) => void;
}

/**
 * Batch add/remove tags across a selection.
 *
 * Safety model for a mistake-prone bulk operation:
 * - live per-tag impact preview ("worship — 12 of 25 videos") before anything runs
 * - remove mode only offers tags that actually exist on the selection (no typos)
 * - explicit confirm step spelling out the change, with a Smart Playlist warning
 *   (tags drive published playlist membership)
 * - plans are idempotent, so retry after a partial failure is safe
 */
export function BatchTagModal({
  opened,
  onClose,
  items,
  entityLabel,
  allTags,
  isPending,
  onApply,
}: BatchTagModalProps) {
  const [mode, setMode] = useState<BatchTagMode>('add');
  const [tags, setTags] = useState<string[]>([]);

  const tagsOnSelection = useMemo(() => TagBatchUtils.tagsOnItems(items), [items]);

  const plan = useMemo(() => TagBatchUtils.planBatch(items, mode, tags), [items, mode, tags]);
  const impact = useMemo(() => TagBatchUtils.impactCounts(items, mode, tags), [items, mode, tags]);

  const plural = (n: number) => `${n} ${entityLabel}${n === 1 ? '' : 's'}`;
  const isRemove = mode === 'remove';
  const changedCount = plan.changed.length;

  const handleModeChange = (value: string) => {
    setMode(value as BatchTagMode);
    setTags([]); // add and remove operate on different tag domains
  };

  const handleSubmit = () => {
    if (tags.length === 0 || changedCount === 0) return;
    const tagList = tags.map((t) => `"${t}"`).join(', ');

    modals.openConfirmModal({
      title: isRemove ? 'Remove tags?' : 'Add tags?',
      children: (
        <Stack gap="xs">
          <Text size="sm">
            {isRemove
              ? `Remove ${tagList} from ${plural(changedCount)}.`
              : `Add ${tagList} to ${plural(changedCount)}.`}
          </Text>
          <Text size="sm" c="dimmed">
            Tags can drive Smart Playlists and feeds — this may change which
            content appears in published playlists.
          </Text>
        </Stack>
      ),
      labels: {
        confirm: isRemove ? 'Remove Tags' : 'Add Tags',
        cancel: 'Cancel',
      },
      confirmProps: isRemove ? { color: 'red' } : {},
      onConfirm: () => onApply(plan, mode, tags),
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Tags — ${plural(items.length)}`}
      size="md"
      closeOnClickOutside={!isPending}
    >
      <Stack gap="md">
        <SegmentedControl
          value={mode}
          onChange={handleModeChange}
          disabled={isPending}
          data={[
            { label: 'Add tags', value: 'add' },
            { label: 'Remove tags', value: 'remove' },
          ]}
        />

        {isRemove && tagsOnSelection.length === 0 ? (
          <Alert icon={<IconTags size={16} />} color="gray">
            None of the selected {entityLabel}s have tags to remove.
          </Alert>
        ) : (
          <TagInput
            value={tags}
            onChange={setTags}
            allTags={isRemove ? tagsOnSelection : allTags}
            allowNewTags={!isRemove}
            disabled={isPending}
            label={isRemove ? 'Tags to remove' : 'Tags to add'}
            description={
              isRemove
                ? 'Only tags present on the selection are offered'
                : 'Pick existing tags or type new ones'
            }
            placeholder={isRemove ? 'Choose a tag to remove...' : 'Add a tag...'}
          />
        )}

        {/* Live impact preview — the user sees exactly what will change */}
        {tags.length > 0 && (
          <Stack gap={4}>
            {tags.map((tag) => {
              const n = impact.get(tag) ?? 0;
              return (
                <Text key={tag} size="sm" c={n === 0 ? 'dimmed' : undefined}>
                  <Text span fw={600}>&ldquo;{tag}&rdquo;</Text>
                  {isRemove
                    ? ` — removed from ${plural(n)}`
                    : ` — added to ${plural(n)}`}
                  {n === 0 && (isRemove ? ' (none have it)' : ' (all already have it)')}
                </Text>
              );
            })}
          </Stack>
        )}

        {plan.overLimit.length > 0 && (
          <Alert icon={<IconAlertTriangle size={16} />} color="orange">
            {plural(plan.overLimit.length)} would exceed the tag limit and will
            be skipped: {plan.overLimit.slice(0, 3).map((i) => `"${i.title}"`).join(', ')}
            {plan.overLimit.length > 3 ? ` and ${plan.overLimit.length - 3} more` : ''}.
          </Alert>
        )}

        <Group justify="space-between" mt="xs">
          <Text size="sm" c="dimmed">
            {tags.length === 0
              ? 'No tags chosen yet'
              : changedCount === 0
                ? `Nothing to change — the selection is already in this state`
                : `${plural(changedCount)} will be updated`}
          </Text>
          <Group gap="sm">
            <Button variant="default" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              color={isRemove ? 'red' : undefined}
              onClick={handleSubmit}
              loading={isPending}
              disabled={tags.length === 0 || changedCount === 0}
            >
              {isRemove ? 'Remove Tags' : 'Add Tags'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
